/**
 * Единый билдер URL фото профиля с серверным ресайзом.
 *
 * Зачем (аудит производительности 2026-08-05): `/profiles/{id}/photo`
 * БЕЗ `?w` отдаёт оригинальный JPEG целиком (замер по user_photos/ —
 * в среднем ~640 КБ). Он вставлялся в слоты 70×70 (слайдер профилей),
 * 24×24 (иконка бейджа в редакторе) и в фото-слоты лаунчера. Пять
 * профилей = ~3 МБ трафика и декодирования ради полоски высотой 70 px.
 *
 * Сервер уже умеет отдавать WebP-миниатюры (`handle_profile_photo`,
 * whitelist ширин PROFILE_THUMB_WIDTHS = {48,96,160,240,400,560}) с
 * `Cache-Control: immutable, max-age=2592000` + ETag — выигрыш давался
 * бесплатно, не хватало только query-параметра.
 *
 * Хелпер общий, чтобы ширины не расползались строковой конкатенацией по
 * шести файлам (как было в references.js) и чтобы невалидная ширина не
 * приводила к молчаливой отдаче оригинала.
 *
 * Подключается ДО ProfilesSlider/custom-style/generation-launcher —
 * см. порядок <script> в index.html.
 */
(function () {
    'use strict';

    /** Ширины, которые реально умеет отдавать бэкенд (PROFILE_THUMB_WIDTHS). */
    const ALLOWED_WIDTHS = [48, 96, 160, 240, 400, 560];

    /**
     * Семантические размеры под конкретные места UI. Держим здесь, чтобы
     * при смене вёрстки менять ширину в одном месте.
     *
     * Правило: CSS-размер × DPR 3 (iPhone), округлённое ВВЕРХ до
     * whitelist. Считаем именно по DPR3, а не 2 — иначе на актуальных
     * телефонах картинка мылит, а ради этого оптимизация и не затевалась
     * (замер: оригинал 102 КБ, w=240 — 7.6 КБ, всё ещё в 13 раз меньше).
     *
     *   badge 24×24 CSS → 72 → 96   (2.0 КБ)
     *   card  70×70 CSS → 210 → 240 (7.6 КБ)
     *   slot  ~200 шир. → 600 → 400 (17.9 КБ, упёрлись в потолок
     *                                whitelist'а; 560 стоит 36 КБ и на
     *                                слот такого размера избыточен)
     */
    const PRESETS = {
        badge: 96,
        card: 240,
        slot: 400,
        preview: 560,   // максимум whitelist'а — для крупных превью
    };

    /**
     * Приводит запрошенную ширину к ближайшей поддерживаемой (вверх).
     * @param {number} width
     * @returns {number}
     */
    function snapWidth(width) {
        const w = Number(width);
        if (!Number.isFinite(w) || w <= 0) return PRESETS.card;
        for (const allowed of ALLOWED_WIDTHS) {
            if (w <= allowed) return allowed;
        }
        return ALLOWED_WIDTHS[ALLOWED_WIDTHS.length - 1];
    }

    /**
     * URL миниатюры по id профиля.
     * @param {number|string} profileId
     * @param {number|string} [size] — число или ключ пресета ('card'|'badge'|…)
     * @returns {string}
     */
    function profilePhotoUrl(profileId, size) {
        const width = typeof size === 'string'
            ? (PRESETS[size] || PRESETS.card)
            : snapWidth(size);
        return `/profiles/${profileId}/photo?w=${width}`;
    }

    /**
     * Добавляет `?w` к УЖЕ готовому URL фото профиля.
     *
     * Нужен там, где на руках только `profile.photo_url` из /api/profiles
     * (бэкенд отдаёт его без параметров). Чужие URL (CDN результатов,
     * data:, абсолютные ссылки) возвращаем нетронутыми — ресайз есть
     * только у эндпоинта профилей.
     *
     * @param {string} url
     * @param {number|string} [size]
     * @returns {string}
     */
    function withProfileThumb(url, size) {
        if (typeof url !== 'string' || !url) return url;
        if (url.includes('?')) return url;              // уже с параметрами
        if (!/^\/profiles\/[^/]+\/photo$/.test(url)) return url;
        const width = typeof size === 'string'
            ? (PRESETS[size] || PRESETS.card)
            : snapWidth(size);
        return `${url}?w=${width}`;
    }

    /**
     * URL ОРИГИНАЛА (без ресайза).
     *
     * Нужен там, где картинку показывают во весь экран и качество —
     * смысл фичи: например long-press «посмотреть исходник» в ленте
     * генераций (SourcePeek растягивает её на ширину карточки). Совать
     * туда миниатюру нельзя — сравнивать исходник с результатом станет
     * не с чем.
     *
     * @param {number|string} profileId
     * @returns {string}
     */
    function profilePhotoOriginal(profileId) {
        return `/profiles/${profileId}/photo`;
    }

    window.ProfilePhoto = {
        url: profilePhotoUrl,
        thumb: withProfileThumb,
        original: profilePhotoOriginal,
        PRESETS,
        ALLOWED_WIDTHS,
        _snapWidth: snapWidth,   // для тестов
    };
})();
